import React, { useEffect, useState } from "react";
import { Calendar, Clock, Search } from "lucide-react";

const MAX_MINUTES = 24 * 60; // 24 giờ

export default function Filters({
  filters,
  setFilters,
  pageSize,
  setPageSize,
  onSubmit,
  loading,
}) {
  const [errors, setErrors] = useState({
    imei: "",
    time: "",
  });

  /* ================= INIT DEFAULT ================= */
  useEffect(() => {
    if (
      !filters.startDate ||
      !filters.endDate ||
      !filters.startTime ||
      !filters.endTime
    ) {
      const now = new Date();
      const after1h = new Date(now.getTime() + 60 * 60 * 1000);

      setFilters((prev) => ({
        ...prev,
        startDate: now.toISOString().slice(0, 10),
        endDate: now.toISOString().slice(0, 10),
        startTime: now.toTimeString().slice(0, 5),
        endTime: after1h.toTimeString().slice(0, 5),
      }));
    }
  }, []);

  /* ================= VALIDATE ================= */
  const validateIMEI = (imei = "") => {
    if (!imei.trim()) return "IMEI không được để trống";
    // if (!/^\d{15}$/.test(imei)) return "IMEI phải gồm đúng 15 chữ số";
    return "";
  };

  const validateDateTimeRange = (
    startDate,
    startTime,
    endDate,
    endTime
  ) => {
    if (!startDate || !startTime || !endDate || !endTime) return false;

    const start = new Date(`${startDate}T${startTime}:00`);
    const end = new Date(`${endDate}T${endTime}:00`);

    const diffMinutes = (end - start) / 60000;
    return diffMinutes > 0 && diffMinutes <= MAX_MINUTES;
  };

  /* ================= HANDLERS ================= */
  const handleIMEIChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    setFilters((prev) => ({ ...prev, imei: value }));

    if (errors.imei) {
      setErrors((prev) => ({
        ...prev,
        imei: validateIMEI(value),
      }));
    }
  };

  const handleIMEIBlur = () => {
    setErrors((prev) => ({
      ...prev,
      imei: validateIMEI(filters.imei),
    }));
  };

  const handleDateTimeChange = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);

    if (
      next.startDate &&
      next.startTime &&
      next.endDate &&
      next.endTime &&
      !validateDateTimeRange(
        next.startDate,
        next.startTime,
        next.endDate,
        next.endTime
      )
    ) {
      setErrors((prev) => ({
        ...prev,
        time: "Khoảng thời gian phải > 0 và không vượt quá 24 giờ",
      }));
    } else {
      setErrors((prev) => ({ ...prev, time: "" }));
    }
  };

  const handleSubmit = () => {
    const imeiError = validateIMEI(filters.imei);

    if (imeiError) {
      setErrors((prev) => ({ ...prev, imei: imeiError }));
      return;
    }

    if (
      !validateDateTimeRange(
        filters.startDate,
        filters.startTime,
        filters.endDate,
        filters.endTime
      )
    ) {
      setErrors((prev) => ({
        ...prev,
        time: "Khoảng thời gian phải > 0 và không vượt quá 24 giờ",
      }));
      return;
    }

    setErrors({ imei: "", time: "" });
    onSubmit();
  };

  /* ================= RENDER ================= */
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* IMEI */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 flex gap-2">
            <Search size={14} /> IMEI <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Nhập IMEI"
            value={filters.imei || ""}
            onChange={handleIMEIChange}
            onBlur={handleIMEIBlur}
            maxLength={100}
            inputMode="numeric"
            className={`w-full bg-gray-50 rounded-lg px-3 py-2 text-sm border
              ${errors.imei ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.imei && (
            <p className="text-xs text-red-500 mt-1">{errors.imei}</p>
          )}
        </div>

        {/* Từ ngày */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 flex gap-2">
            <Calendar size={14} /> Từ ngày *
          </label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              handleDateTimeChange("startDate", e.target.value)
            }
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Đến ngày */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 flex gap-2">
            <Calendar size={14} /> Đến ngày *
          </label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              handleDateTimeChange("endDate", e.target.value)
            }
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Từ giờ */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 flex gap-2">
            <Clock size={14} /> Từ giờ *
          </label>
          <input
            type="time"
            value={filters.startTime}
            onChange={(e) =>
              handleDateTimeChange("startTime", e.target.value)
            }
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {/* Đến giờ */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 flex gap-2">
            <Clock size={14} /> Đến giờ *
          </label>
          <input
            type="time"
            value={filters.endTime}
            onChange={(e) =>
              handleDateTimeChange("endTime", e.target.value)
            }
            className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Lỗi thời gian */}
      {errors.time && (
        <p className="text-xs text-red-500 mt-2">{errors.time}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Hiển thị</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-gray-50 border border-gray-300 rounded-lg px-2 py-1 text-gray-900"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>bản ghi / trang</span>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm flex gap-2 items-center font-semibold transition-colors disabled:opacity-50"
        >
          <Search size={16} />
          Tìm kiếm
        </button>
      </div>
    </form>
  );
}
